import type React from 'react'
import type { Appointment } from '../api/client'
import { clientName } from '../calendar/names'
import { formatLength, NO_CATEGORY_COLOUR } from '../catalog/format'
import { heldLine } from '../payments/deposit'
import { paymentStateLabel, statusLabel } from '../payments/status'
import FormChip from '../forms/FormChip'
import VisitPrice from './VisitPrice'
import './AppointmentRow.css'

type Tone = 'neutral' | 'positive' | 'warning' | 'danger'

// The tags under a booking: its own state (only when it isn't simply booked),
// what's been paid (A1's payment_state), and "Outside hours". Each carries a
// word, so nothing is said by colour alone. "Card saved" appears only while
// card payments are shown at all (cardShown).
export function appointmentTags(a: Appointment, cardShown = false): { text: string; tone: Tone }[] {
  const tags: { text: string; tone: Tone }[] = []
  if (a.status !== 'booked') {
    const off = a.status === 'cancelled' || a.status === 'no_show'
    tags.push({ text: statusLabel(a.status), tone: off ? 'danger' : 'neutral' })
  }
  if (!a.awaiting_deposit) {
    const payment = paymentStateLabel(a.payment_state, a.paid_method, a.amount_due_pence)
    if (payment) {
      const tone: Tone =
        a.payment_state === 'paid' || a.payment_state === 'package' || a.payment_state === 'paid_before' ? 'positive' : a.payment_state === 'to_pay' ? 'warning' : 'neutral'
      tags.push({ text: payment, tone })
    }
  }
  if (cardShown && a.client_has_card) tags.push({ text: 'Card saved', tone: 'neutral' })
  if (a.outside_hours) tags.push({ text: 'Outside working hours', tone: 'neutral' })
  return tags
}

// AppointmentRow is one booking as Today and the Calendar's day both draw it
// (review 2026-10-14): a category stripe, the client, the treatment line with
// its price, the deposit hold if any, and the tags. A completed one is drawn
// grey, so what's still to do stands out. The whole row is the button that
// opens the appointment.
export default function AppointmentRow({ appointment: a, timezone, className, showForm, cardShown, onOpen }: {
  appointment: Appointment
  timezone?: string
  // A screen's own hook for its layout (and its tests): 'day-booking', …
  className?: string
  // Today marks a client whose consultation form is still missing.
  showForm?: boolean
  // Card payments are on, so the client's saved card is worth a tag.
  cardShown?: boolean
  onOpen: () => void
}) {
  const held = a.awaiting_deposit
  const off = a.status === 'cancelled' || a.status === 'no_show'
  const done = a.status === 'completed'
  const tags = appointmentTags(a, cardShown)
  const classes = ['appt-row', held ? 'appt-row--held' : '', off ? 'appt-row--off' : '', done ? 'appt-row--done' : '', className ?? ''].filter(Boolean).join(' ')
  return (
    <button
      type="button"
      className={classes}
      style={{ '--cat': a.service.colour ?? NO_CATEGORY_COLOUR } as React.CSSProperties}
      onClick={onOpen}
    >
      <span className="appt-row__name">
        {clientName(a.client)}
        {showForm && a.form_status === 'needed' && <>{' '}<FormChip status="needed" /></>}
      </span>
      <span className="appt-row__meta">
        {a.service.name} · {formatLength(a.service.duration_minutes)} · <VisitPrice pence={a.total_price_pence} paymentState={a.payment_state} />
      </span>
      {held && <span className="appt-row__held">{heldLine(a, timezone)}</span>}
      {tags.length > 0 && (
        <span className="appt-row__tags appointment-state">
          {tags.map((t) => (
            <span key={t.text} className={t.tone === 'neutral' ? 'tag' : `tag tag--${t.tone}`}>{t.text}</span>
          ))}
        </span>
      )}
    </button>
  )
}
